/***************************************************************************
 * EditingRowBannerOverlay — a single RowBanner per DataGrid, portaled into
 * the grid's root element and absolutely positioned to sit directly below
 * the currently-editing row and span the full grid width.
 *
 * EditableRow would otherwise render the banner inside every row-section
 * DOM element (left-pinned, main, right-pinned), producing 2–3 duplicated
 * banners.  Lifting the banner to the grid level solves that and also lets
 * the banner span the full visible width — starting at the pinned-left
 * edge and extending across, per the product spec.
 ***************************************************************************/

import {GridApi} from "@mui/x-data-grid-pro";
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {RowBanner} from "./RowBanner";
import {RowBannerState, useRowEditContext} from "./RowEditContext";

interface Props
{
   apiRef: React.MutableRefObject<GridApi>;
}

export const EditingRowBannerOverlay: React.FC<Props> = ({apiRef}) =>
{
   const ctx = useRowEditContext();
   const editingRowId = ctx?.editingRowId ?? null;
   const bannerKind: RowBannerState["kind"] = ctx?.rowBannerState.kind ?? "none";

   const [rootEl, setRootEl] = useState<HTMLElement | null>(null);
   const [style, setStyle] = useState<React.CSSProperties>({display: "none"});

   ///////////////////////////////////////////////////////////////////////////////////
   // Resolve the grid root once it's mounted (DataGridPro populates rootElementRef  //
   // during its first layout effect which may run after our own mount).             //
   ///////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      let cancelled = false;
      function tryResolve()
      {
         const root = apiRef.current?.rootElementRef?.current as HTMLElement | undefined;
         if (root)
         {
            setRootEl(root);
            return true;
         }
         return false;
      }
      if (!tryResolve())
      {
         const id = setInterval(() =>
         {
            if (cancelled)
            {
               return;
            }
            if (tryResolve())
            {
               clearInterval(id);
            }
         }, 50);
         return () =>
         {
            cancelled = true;
            clearInterval(id);
         };
      }
   }, [apiRef]);

   const updateStyle = useCallback(() =>
   {
      if (!rootEl || editingRowId == null)
      {
         setStyle({display: "none"});
         return;
      }
      const rowEl = apiRef.current?.getRowElement?.(editingRowId) as HTMLElement | null;
      if (!rowEl)
      {
         setStyle({display: "none"});
         return;
      }
      const rootRect = rootEl.getBoundingClientRect();
      const rowRect  = rowEl.getBoundingClientRect();

      ///////////////////////////////////////////////////////////////////////////////////
      // Anchor the banner just below the actual cell-content bottom of the editing    //
      // row.  Using rowRect.bottom is unreliable — depending on whether the row's     //
      // height was explicitly set (numeric) or measured (auto), the reported row      //
      // bottom may or may not include the sibling spacer div.  Querying the cell      //
      // bottom directly gives a stable reference point: banner sits immediately under //
      // the input row with a small gap.                                               //
      ///////////////////////////////////////////////////////////////////////////////////
      const cells = rowEl.querySelectorAll(".MuiDataGrid-cell");
      let cellBottom = rowRect.top;
      cells.forEach((c) =>
      {
         const r = (c as HTMLElement).getBoundingClientRect();
         if (r.bottom > cellBottom)
         {
            cellBottom = r.bottom;
         }
      });
      if (cellBottom === rowRect.top)
      {
         cellBottom = rowRect.bottom;
      }
      const top = cellBottom - rootRect.top + 4;  // 4px gap between inputs and banner

      ///////////////////////////////////////////////////////////////////////////////
      // Hide the banner when the editing row has been scrolled outside the grid's //
      // visible viewport.                                                         //
      ///////////////////////////////////////////////////////////////////////////////
      const scroller = rootEl.querySelector(".MuiDataGrid-virtualScroller") as HTMLElement | null;
      const viewport = scroller ? scroller.getBoundingClientRect() : rootRect;
      if (rowRect.top + rowRect.height <= viewport.top || rowRect.top >= viewport.bottom)
      {
         setStyle({display: "none"});
         return;
      }

      setStyle({
         position:      "absolute",
         top,
         left:          0,
         right:         0,
         zIndex:        1,
      });
   }, [apiRef, rootEl, editingRowId]);

   useLayoutEffect(() =>
   {
      updateStyle();
   }, [updateStyle]);

   //////////////////////////////////////////////////////////////////////////////////
   // Poll updateStyle across a short burst after editingRowId or bannerKind change. //
   // When the editing row transitions in, DataGrid swaps cells (they're replaced,  //
   // not resized) as the Formik tree mounts and inputs hydrate — so the            //
   // ResizeObserver we attached to the OLD cells never fires for the NEW cells'    //
   // final layout.  A few rAFs over the first ~500 ms catches the settled layout.  //
   //////////////////////////////////////////////////////////////////////////////////
   const burstRef = useRef<number | null>(null);
   useEffect(() =>
   {
      if (editingRowId == null)
      {
         return;
      }
      let attempts = 0;
      const MAX = 30;  // ~500 ms at 60fps
      const tick = () =>
      {
         updateStyle();
         attempts++;
         if (attempts < MAX)
         {
            burstRef.current = requestAnimationFrame(tick);
         }
      };
      burstRef.current = requestAnimationFrame(tick);
      return () =>
      {
         if (burstRef.current != null)
         {
            cancelAnimationFrame(burstRef.current);
            burstRef.current = null;
         }
      };
   }, [editingRowId, bannerKind, updateStyle]);

   useEffect(() =>
   {
      if (!rootEl)
      {
         return;
      }
      const ro = new ResizeObserver(updateStyle);
      ro.observe(rootEl);
      ///////////////////////////////////////////////////////////////////////////////////
      // Also observe the editing row itself so the banner re-anchors when input cells //
      // hydrate and grow their rendered height — otherwise the banner's first-paint   //
      // position is based on pre-hydration cell heights and only corrects itself on   //
      // the next window resize.                                                       //
      ///////////////////////////////////////////////////////////////////////////////////
      if (editingRowId != null)
      {
         const rowEl = apiRef.current?.getRowElement?.(editingRowId) as HTMLElement | null;
         if (rowEl)
         {
            ro.observe(rowEl);
         }
         rowEl?.querySelectorAll(".MuiDataGrid-cell").forEach((c) => ro.observe(c as HTMLElement));
      }
      const scroller = rootEl.querySelector(".MuiDataGrid-virtualScroller") as HTMLElement | null;
      if (scroller)
      {
         scroller.addEventListener("scroll", updateStyle);
      }
      window.addEventListener("resize", updateStyle);
      return () =>
      {
         ro.disconnect();
         if (scroller)
         {
            scroller.removeEventListener("scroll", updateStyle);
         }
         window.removeEventListener("resize", updateStyle);
      };
   }, [rootEl, updateStyle, editingRowId, apiRef]);

   const content = useMemo(() =>
   {
      if (editingRowId == null)
      {
         return null;
      }
      return (
         <div style={style} className="qqq-row-edit-banner-overlay">
            <div>
               <RowBanner />
            </div>
         </div>
      );
   }, [editingRowId, style]);

   if (!rootEl || !content)
   {
      return null;
   }
   return createPortal(content, rootEl);
};
